import { useCallback, useState } from 'react'
import { isString } from 'remeda'
import { gql, type TypedDocumentNode } from '@apollo/client'
import { useWishlist } from '@components/WishlistProvider/WishlistProvider'
import { LoadingIcon } from '@velasca/icons/loading'
import { WishlistWhiteIcon } from '@velasca/icons/wishlist-white'
import { apolloClient } from '@velasca/utils/apollo-client'
import { $settings } from '@velasca/utils/globals'
import { trackEvent } from '@velasca/utils/track-event'
import * as S from './WishlistCTA.styles'

interface WishlistCTAProps {
  layout: 'tile' | 'tile-inverted' | 'button' | 'absolute-button'
  productData: {
    handle: string
    variantId?: string
    productId: string
    /** If provided by the caller, indicates the user chose the variant explicitly. */
    variantSelectedByUser?: boolean
    /** Product title for analytics. */
    title?: string
    /** Product price for analytics. */
    price?: string | number
    /** Brand name for analytics. "Man", "Woman", etc. */
    brand?: string
    /** Origin for analytics. Product Page, Related products carouel, Recent products carousel, etc. */
    origin?: string
    categoryLevel1?: string
    categoryLevel2?: string
    categoryLevel3?: string
  }
}

interface GetProductFirstVariantData {
  productByHandle: {
    id: string
    variants: {
      edges: {
        node: {
          id: string
        }
      }[]
    }
  } | null
}

interface GetProductFirstVariantVariables {
  handle: string
}

const GET_PRODUCT_FIRST_VARIANT: TypedDocumentNode<
  GetProductFirstVariantData,
  GetProductFirstVariantVariables
> = gql`
  query GetProductFirstVariant($handle: String!) {
    productByHandle(handle: $handle) {
      id
      variants(first: 1) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`

export const WishlistCTA = ({ layout, productData }: WishlistCTAProps) => {
  const { isActive, addProduct, isProductInWishlist } = useWishlist()
  const [isLoading, setIsLoading] = useState(false)

  // Check if product is already in wishlist
  const isInWishlist = isProductInWishlist(productData.productId)

  const handleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation()
      e.preventDefault()

      // If product is already in wishlist, don't do anything for now
      // If it's loading, also don't do anything
      if (isInWishlist || isLoading) {
        return
      }

      const addToWishlistAsync = async () => {
        setIsLoading(true)
        try {
          const { variantId: currentVariantId, handle } = productData
          let variantId = currentVariantId

          // If variantId is not provided, fetch the first variant from Shopify
          if (!variantId) {
            const { data } = await apolloClient.query({
              query: GET_PRODUCT_FIRST_VARIANT,
              variables: { handle },
            })

            if (data?.productByHandle?.variants?.edges?.[0]?.node?.id) {
              variantId = data.productByHandle.variants.edges[0].node.id
            } else {
              throw new Error('No variants found for this product')
            }
          }

          // Add product to wishlist with the variantId
          await addProduct({
            ...productData,
            variantId,
          })

          const {
            productId,
            title,
            price: productPrice,
            origin,
            brand,
            categoryLevel1,
            categoryLevel2,
            categoryLevel3,
          } = productData

          if (title) {
            const price = isString(productPrice)
              ? parseFloat(productPrice.replaceAll(/[^0-9.,]/g, ''))
              : productPrice

            const ga4ProductData = {
              item_id: productId,
              item_name: title,
              item_brand: brand,
              item_category: categoryLevel1,
              item_category2: categoryLevel2,
              item_category3: categoryLevel3,
              item_list_name: origin,
              price,
              quantity: 1,
              currency: $settings.currency,
            }

            trackEvent({
              googleAnalytics: {
                eventName: 'add_to_wishlist',
                data: {
                  currency: ga4ProductData.currency,
                  value: ga4ProductData.price || 0,
                  items: [ga4ProductData],
                },
              },
            })

            trackEvent({
              teads: {
                eventName: 'AddToWishlist',
                data: {
                  name: title,
                  price,
                  currency: $settings.currency,
                },
              },
            })
          }
        } catch (error) {
          console.error('Error adding product to wishlist:', error)
        } finally {
          setIsLoading(false)
        }
      }

      addToWishlistAsync().catch((error) => {
        console.error('Error in addToWishlistAsync:', error)
      })
    },
    [isInWishlist, isLoading, productData, addProduct],
  )

  if (!isActive) {
    return null
  }

  return (
    <S.WishlistCTAContainer
      className={`wishlist-cta ${layout}`}
      layout={layout}
      $isFilled={isInWishlist}
      $isLoading={isLoading}
      role="button"
      tabIndex={0}
      aria-pressed={isInWishlist}
      aria-disabled={isLoading}
      aria-label={
        isLoading
          ? 'Adding to wishlist...'
          : isInWishlist
            ? 'Already in wishlist'
            : 'Add to wishlist'
      }
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick(e)
        }
      }}
    >
      {isLoading ? (
        <LoadingIcon aria-hidden="true" />
      ) : (
        <WishlistWhiteIcon aria-hidden="true" />
      )}
    </S.WishlistCTAContainer>
  )
}
